/**
 * Working-day due date calculation. Mirrors supernova financialclose/utils/workingDays.js.
 * Saturday and Sunday are excluded; public holidays are not.
 */

const SATURDAY = 6;
const SUNDAY = 0;

/**
 * @param {Date} date
 * @param {number[]} [weekendDays=[0,6]]
 * @returns {boolean}
 */
export function isWeekendDate(date, weekendDays = [SUNDAY, SATURDAY]) {
  return weekendDays.includes(date.getDay());
}

/**
 * @param {Date} date
 * @returns {Date}
 */
export function toStartOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * @param {Date} date
 * @returns {string} YYYY-MM-DD
 */
export function formatIsoDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Normalize a Date to a calendar day for working-day math.
 * API/Mongo dates stored as UTC midnight (calendar Y-M-D) must keep UTC components,
 * not local start-of-day (which shifts the day on hosts west of UTC).
 *
 * @param {Date} date
 * @returns {Date|null}
 */
export function normalizeToCalendarDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  const isUtcMidnight =
    date.getUTCHours() === 0
    && date.getUTCMinutes() === 0
    && date.getUTCSeconds() === 0
    && date.getUTCMilliseconds() === 0;
  if (isUtcMidnight) {
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }
  return toStartOfDay(date);
}

/**
 * @param {Date|string|number|null|undefined} value
 * @returns {Date|null}
 */
export function parseLocalDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return normalizeToCalendarDate(value);
  }
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  }
  const parsed = new Date(value);
  return normalizeToCalendarDate(parsed);
}

/**
 * @param {Date} referenceDate
 * @param {number} workingDays
 * @param {{ weekendDays?: number[] }} [options]
 * @returns {Date}
 */
export function calculateWorkingDayDueDate(referenceDate, workingDays, options = {}) {
  const { weekendDays = [SATURDAY, SUNDAY] } = options;
  const current = toStartOfDay(referenceDate);

  if (workingDays === 0) {
    while (isWeekendDate(current, weekendDays)) {
      current.setDate(current.getDate() + 1);
    }
    return current;
  }

  const direction = workingDays > 0 ? 1 : -1;
  let remaining = Math.abs(workingDays);
  while (remaining > 0) {
    current.setDate(current.getDate() + direction);
    if (!isWeekendDate(current, weekendDays)) {
      remaining -= 1;
    }
  }
  return current;
}

/**
 * Allowed due-date window for cycle checklist tasks: anchor ± maxWorkingDays (Sat/Sun excluded).
 * Anchor is cycle end when `cycleEndDate` parses; otherwise today.
 *
 * Typical consumer: `MAX_DUE_DATE_DAYS` from `constants.js` (200 working days each side).
 * Example: cycle end 31-Dec-2026 → min ≈ cycle end − 200 WD, max ≈ cycle end + 200 WD.
 * Horizon bulk due-date (`HorizonLanding.vue`) uses `minIso` / `maxIso` from here — not “today only”.
 *
 * Not used for Information Manager request due dates (see `AddEditRequestModal.vue` calendar cap).
 *
 * @param {Date|string|null|undefined} cycleEndDate
 * @param {number} [maxWorkingDays=200]
 * @returns {{ minDate: Date, maxDate: Date, minIso: string, maxIso: string, usesCycleEnd: boolean }}
 */
export function getCycleDueDateWindow(cycleEndDate, maxWorkingDays = 200) {
  const today = toStartOfDay(new Date());
  const cycleEnd = parseLocalDate(cycleEndDate);
  const usesCycleEnd = !!cycleEnd;
  const anchor = usesCycleEnd ? cycleEnd : today;
  const minDate = calculateWorkingDayDueDate(anchor, -maxWorkingDays);
  const maxDate = calculateWorkingDayDueDate(anchor, maxWorkingDays);

  return {
    minDate,
    maxDate,
    minIso: formatIsoDate(minDate),
    maxIso: formatIsoDate(maxDate),
    usesCycleEnd,
  };
}
